// Pulled from .env so they can be overridden per-environment.
// All values are public (relay URLs, public bot pubkey, public Census API).

const fallbackRelays = [
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://relay.primal.net',
  'wss://nostr.mom',
]

export const RELAYS = (import.meta.env.VITE_RELAYS || fallbackRelays.join(','))
  .split(',')
  .map((r) => r.trim())
  .filter(Boolean)

export const BOT_PUBKEY =
  import.meta.env.VITE_BOT_PUBKEY ||
  'f914ac0d4057d14d4d28d3baf5289fb6f9a97118455513920c7044f0a31b324f'

// Default to the relative path; Vite (dev) and Vercel (prod) proxy it to
// the Census Geocoder, which doesn't send CORS headers of its own.
export const CENSUS_API =
  import.meta.env.VITE_CENSUS_API || '/api/census'

export const POLL_TAG = 'ThePeopleVote'

// App identity — overridable per environment.
export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Verdant'
export const APP_TAGLINE = import.meta.env.VITE_APP_TAGLINE || 'Growing a better democracy.'

// The sentiment-only disclaimer. Rendered identically in the feed
// banner, the per-poll page, and the Analytics tab so the voice
// doesn't drift across surfaces. Bump this single string when the
// disclaimer changes.
// UX58: benefit-driven reframe. The previous copy read as a legal
// shield ("pseudonymous community votes, not an official poll");
// mainstream users bounce off that. New copy leads with the privacy
// win, defines what we track in one plain line, and lets the Footer
// Privacy modal carry the detailed version for anyone who wants it.
export const DISCLAIMER_TEXT =
  "Your vote is private. We track your zip code, not your identity."

// localStorage keys
export const LS_JURISDICTION = 'verdant_jurisdiction'
// Whether the topic-chip row in FeedControls is rendered expanded
// (all chips visible) or collapsed (All topics + selected + toggle).
// UX25 — default collapsed so the row doesn't dominate the viewport.
export const LS_TOPICS_EXPANDED = 'verdant_topics_expanded'
// Signed events the user has authored (polls + votes). Hydrated into the
// feed on mount so the user always sees their own posts even if a relay
// silently dropped them.
export const LS_MY_EVENTS = 'verdant_my_events'
export const MY_EVENTS_CAP = 200
// kind:0 profile cache — one localStorage entry per pubkey we've seen.
// Looked up on mount for instant render; refreshed in background by
// useProfile.
export const LS_PROFILE_PREFIX = 'verdant_profile_'
export const PROFILE_CAP_BYTES = 50_000

// T16: NIP-05 verification result cache. One entry per pubkey we've
// checked, keyed by pubkey; the stored value includes the nip05 string
// so a profile update that changes the handle invalidates the cache
// automatically. 7-day TTL — long enough that the common hot path
// (same user returning) hits the cache, short enough that a user who
// loses control of a domain is correctly re-verified within a week.
export const LS_NIP05_PREFIX = 'verdant_nip05_'
export const NIP05_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000

// UX41: ephemeral-identity vote counter and upgrade-prompt state. We
// generate a Nostr key silently on a user's first vote so they can
// participate without an auth modal; after a few votes we offer to
// save their progress with Google.
export const LS_EPHEMERAL_VOTES = 'verdant_ephemeral_votes'
export const LS_UPGRADE_PROMPT = 'verdant_upgrade_prompt'
export const UPGRADE_PROMPT_THRESHOLD = 3   // show after Nth vote
export const UPGRADE_PROMPT_SNOOZE_VOTES = 5  // re-prompt after N more
                                               // votes if dismissed

// Official Congress.gov / CRS "Policy Areas" — the 32 broad categories
// the Congressional Research Service assigns to every federal bill.
// Returned by the Congress.gov API as the `policyArea.name` field. Used
// here as the canonical taxonomy for the Propose form and the Analytics
// topic filter.
//
// Source: https://www.congress.gov/help/field-values/policy-area
export const CATEGORIES = [
  'Agriculture and Food',
  'Animals',
  'Armed Forces and National Security',
  'Arts, Culture, Religion',
  'Civil Rights and Liberties, Minority Issues',
  'Commerce',
  'Congress',
  'Crime and Law Enforcement',
  'Economics and Public Finance',
  'Education',
  'Emergency Management',
  'Energy',
  'Environmental Protection',
  'Families',
  'Finance and Financial Sector',
  'Foreign Trade and International Finance',
  'Government Operations and Politics',
  'Health',
  'Housing and Community Development',
  'Immigration',
  'International Affairs',
  'Labor and Employment',
  'Law',
  'Native Americans',
  'Public Lands and Natural Resources',
  'Science, Technology, Communications',
  'Social Sciences and History',
  'Social Welfare',
  'Sports and Recreation',
  'Taxation',
  'Transportation and Public Works',
  'Water Resources Development',
]

export const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],
  ['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],
  ['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],
  ['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],
  ['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],
  ['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],
  ['WI','Wisconsin'],['WY','Wyoming'],['DC','District of Columbia'],
]
